import { useEffect, useRef } from 'react';
import { createTinyUrqlClient } from '@packages/gql/src/tinyClient';
import type { GTMEventGlycerinSessionCartInformation } from '@packages/tracking/src/types/events';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { type Client } from 'urql';
import { useSession } from '@packages/shared/src/hooks/useSession/useSession';
import { SessionCartInfoDocument } from '@packages/gql/generated/shopping/SessionCartInfoDocument';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';

declare global {
  interface Window {
    [key: string]: number;
  }
}

/* GraphQL */ `
  query SessionCartInfo($locale: String!) {
    basket(locale: $locale, skipRecalculation: true) {
      itemCount
      subTotal {
        amount
        currency
      }
      lineItems {
        extra {
          description
          variation
          size
          promotion
          breadcrumbs
          mkz
          category
        }
        quantity
        product {
          orderNumber
        }
      }
    }
  }
`;

/**
 * This hook is used to notify Glycerin of the cart information at the start of a new session.
 */
export const useSessionCartInfoTracking = () => {
  const dispatchGtmEvent = useTracking();
  const { jwtToken } = useSession();
  const { locale } = useI18n();
  const client = useRef<Client>(null);

  useEffect(() => {
    const scheduleEvent = async () => {
      if (!client.current) {
        client.current = createTinyUrqlClient();
      }

      if (jwtToken) {
        const { data, error } = await client.current.query(
          SessionCartInfoDocument,
          { locale },
          { fetchOptions: { headers: { 'x-ec-token': jwtToken } } },
        );

        if (error) {
          return;
        }

        const basket = data?.basket;

        window.basketCount = basket?.itemCount || 0;

        dispatchGtmEvent<GTMEventGlycerinSessionCartInformation>({
          event: 'SessionCartInformation',
          SessionCartInformationData:
            basket && basket.itemCount > 0
              ? {
                  persistentCart: true,
                  currency:
                    basket.subTotal.currency === 'EUR' || basket.subTotal.currency === 'CHF'
                      ? basket.subTotal.currency
                      : 'EUR',
                  persistentValue: basket.subTotal.amount,
                  custom: {
                    gtmData: {
                      // NOTE using 'unknown' for missing values is a spot decision, was not defined in MOBV-604
                      products: basket.lineItems.map((lineItem) => ({
                        productName: lineItem.extra?.description ?? 'unknown',
                        variant: lineItem.extra?.variation ?? 'unknown',
                        size: lineItem.extra?.size ?? 'unknown',
                        orderNumber: lineItem.product.orderNumber,
                        promotion: lineItem.extra?.promotion ?? 'unknown',
                        quantity: lineItem.quantity,
                        categoryShops: lineItem.extra?.breadcrumbs?.map(
                          (name, index, breadcrumbs) => {
                            const isLastEntry = index === breadcrumbs.length - 1;
                            // according to the type definition, the id only exists for the last breadcrumb entry
                            const id =
                              isLastEntry && lineItem.extra?.category
                                ? lineItem.extra.category
                                : undefined;

                            return {
                              name,
                              id,
                            };
                          },
                        ),
                        categoryMkz: lineItem.extra?.mkz ?? undefined,
                      })),
                    },
                  },
                }
              : {
                  persistentCart: false,
                },
        });
      }
    };

    document.addEventListener('window.glycerin.session.start', scheduleEvent);

    return () => {
      document.removeEventListener('window.glycerin.session.start', scheduleEvent);
    };
  }, [dispatchGtmEvent, jwtToken, locale]);
};
